// src/components/Login.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../AuthStyles.css';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');


export default function Login() {
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }, []);
  const navigate = useNavigate();

  // --- ログイン ---
  const [email, setEmail] = useState(localStorage.getItem('authEmail') || '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loginMsg, setLoginMsg] = useState('');

  const handleLogin = async () => {
    setLoginMsg('');
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'ログインに失敗しました');
      localStorage.setItem('authEmail', email);
      navigate(`/store-page?email=${encodeURIComponent(email)}`);
    } catch (e) {
      setLoginMsg(e.message || 'サーバーエラー');
    }
  };

  // --- パスワード再設定（初期は非表示、リンクで展開） ---
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetCodeOk, setResetCodeOk] = useState(null); // true/false/null
  const [newPw, setNewPw] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const [resetInfo, setResetInfo] = useState('');

  const isDesktop = useMemo(() => window.matchMedia('(min-width: 1024px)').matches, []);

  const sendResetCode = async () => {
    setResetMsg('');
    setResetInfo('');
    setResetCodeOk(null);
    try {
      const res = await fetch(`${API_BASE}/auth/password/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || '送信に失敗しました');
      setResetInfo('認証コードを送信しました。メールをご確認ください。');
    } catch (e) {
      setResetMsg(e.message || 'サーバーエラー');
    }
  };

  const verifyResetCode = async () => {
    setResetMsg('');
    setResetInfo('');
    try {
      const res = await fetch(`${API_BASE}/auth/password/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail, code: resetCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResetCodeOk(false);
        throw new Error(data?.error || '認証コードが違います');
      }
      setResetCodeOk(true);
      setResetInfo('承認されました');
    } catch (e) {
      setResetMsg(e.message || 'サーバーエラー');
    }
  };

  const doReset = async () => {
    setResetMsg('');
    setResetInfo('');
    try {
      const res = await fetch(`${API_BASE}/auth/password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail, code: resetCode, newPassword: newPw }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || '再設定に失敗しました');
      setResetInfo('パスワードを更新しました。新しいパスワードでログインしてください。');
      setResetCodeOk(null);
      setResetCode('');
      setNewPw('');
      setShowReset(false); // 更新後は閉じる
    } catch (e) {
      setResetMsg(e.message || 'サーバーエラー');
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:64px_64px]" />
      
      <div className="max-w-2xl w-full space-y-8 relative z-10">
        {/* --- ログインカード --- */}
        <div className="bg-card/80 backdrop-blur-md border border-border/50 rounded-3xl p-8 shadow-2xl ring-1 ring-white/10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent text-3xl font-bold">
              店舗用ログイン
            </h2>
            <p className="text-muted-foreground mt-2">店舗管理ページにアクセス</p>
          </div>

          <div className="space-y-8">
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                メールアドレス
              </label>
              <input
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors"
                placeholder="例）you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && email && password) {
                    handleLogin();
                  }
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                パスワード
              </label>
              <div className="relative flex w-full">
                <input
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors min-w-0"
                  type={showPw ? 'text' : 'password'}
                  placeholder="例）8文字以上の英数字"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && email && password) {
                      handleLogin();
                    }
                  }}
                />
                <button
                  type="button"
                  className="flex-shrink-0 px-3 py-3 text-sm text-gray-600 hover:text-gray-800 transition-colors bg-gray-100 hover:bg-gray-200 rounded-r-lg border border-l-0 border-gray-300"
                  onClick={() => setShowPw((s) => !s)}
                >
                  {showPw ? '隠す' : '表示'}
                </button>
              </div>
              <button
                type="button"
                className="text-sm text-purple-600 hover:text-purple-700 mt-2 transition-colors"
                onClick={() => setShowReset(true)}
              >
                パスワードをお忘れの方
              </button>
            </div>

            <button
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-4 px-6 rounded-xl transition-all transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2 focus:ring-offset-background shadow-lg hover:shadow-xl"
              onClick={handleLogin}
            >
              ログイン
            </button>

            <div className="text-center">
              <Link 
                to="/register" 
                className="text-purple-600 hover:text-purple-700 font-medium transition-colors"
              >
                新規登録へ
              </Link>
            </div>

            {loginMsg && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-600 px-4 py-3 rounded-lg">
                {loginMsg}
              </div>
            )}
          </div>
        </div>

        {/* --- パスワード再設定カード --- */}
        {showReset && (
          <div className="bg-card/80 backdrop-blur-md border border-border/50 rounded-3xl p-8 shadow-2xl ring-1 ring-white/10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h2 className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent text-2xl font-bold">
                パスワード再設定
              </h2>
              <p className="text-muted-foreground mt-2">ご登録メールにコードを送信します</p>
            </div>

            <div className="space-y-8">
              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  メールアドレス
                </label>
                <div className="flex gap-2">
                  <input
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors"
                    placeholder="例）you@example.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                  />
                  <button 
                    className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors"
                    onClick={sendResetCode}
                  >
                    コード送信
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  認証コード
                </label>
                <div className="flex gap-2">
                  <input
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors"
                    placeholder="6桁コード"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                  />
                  <button 
                    className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors"
                    onClick={verifyResetCode}
                  >
                    認証する
                  </button>
                </div>
                {resetCodeOk === true && (
                  <div className="mt-2 text-green-600 text-sm">承認されました</div>
                )}
                {resetCodeOk === false && (
                  <div className="mt-2 text-red-600 text-sm">認証コードが違います</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  新しいパスワード
                </label>
                <div className="relative flex w-full">
                  <input
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors min-w-0"
                    type={showNewPw ? 'text' : 'password'}
                    placeholder="8文字以上の英数字"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                  />
                  <button
                    type="button"
                    className="flex-shrink-0 px-3 py-3 text-sm text-gray-600 hover:text-gray-800 transition-colors bg-gray-100 hover:bg-gray-200 rounded-r-lg border border-l-0 border-gray-300"
                    onClick={() => setShowNewPw((s) => !s)}
                  >
                    {showNewPw ? '隠す' : '表示'}
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  className="flex-1 px-4 py-3 border border-border text-foreground hover:bg-accent rounded-lg transition-colors"
                  onClick={() => setShowReset(false)}
                >
                  閉じる
                </button>
                <button
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={doReset}
                  disabled={!resetEmail || !resetCode || !newPw}
                >
                  パスワードを更新
                </button>
              </div>

              {resetInfo && (
                <div className="bg-green-500/10 border border-green-500/20 text-green-600 px-4 py-3 rounded-lg">
                  {resetInfo}
                </div>
              )}
              {resetMsg && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-600 px-4 py-3 rounded-lg">
                  {resetMsg}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
